import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, unref as _unref, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, normalizeClass as _normalizeClass, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-b2fb9730"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "location-info-content" }
const _hoisted_2 = { class: "filter-bar" }
const _hoisted_3 = { class: "filter-left" }
const _hoisted_4 = { class: "focus-time" }
const _hoisted_5 = { class: "filter-label" }
const _hoisted_6 = { class: "filter-right" }

import { computed, reactive, ref, watch, nextTick, PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import CmTable from '@/components/cmTable/index.vue'
import { TableOpt, TableActOpt, PageOption } from '@/components/cmTable/types'
import { FormSize } from '@/components/cmForm/types'
import dayjs from 'dayjs'
import { TimeRange } from '@/views/dashboard/_components/time-range-select/time-range.enum'
import { formatTimeStamp, getTimeRangeByEnum } from '@/utils/time'
import { useCmTableListRefresh } from '@/hooks/use-cm-table-list-refresh'
import ExportDropdown from '@/views/dashboard/_components/export-dropdown/export-dropdown.vue'
import { ExportType } from '@/views/dashboard/_components/export-dropdown/export-dropdown.service'
import LocationMap from './location-map.vue'
import { formatPointTime } from './marker-icon'
import { useLocationSourceLabels } from './location-source'
import { LocationInfoService, List, RequestParams } from './service'
import type { ClientItem } from '@/views/dashboard/_type/client-item'

interface MapFocusPoint {
  latitude: number
  longitude: number
  locationTime: string
}

export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  props: {
  modelValue: {
    type: Boolean,
    default: false
  },
  terminalInfo: {
    type: Object as PropType<ClientItem>,
    default: () => ({})
  }
},
  emits: ['update:modelValue'],
  setup(__props, { emit: __emit }) {

const props = __props
const emit = __emit

const { t, locale } = useI18n()
const injector = useReflectiveInjector([LocationInfoService])
const service = injector.get(LocationInfoService)

const tableRef = ref()
const { refreshList } = useCmTableListRefresh(tableRef)
const mapRef = ref()

/** 抽屉内 tab：默认定位历史 */
const activeTab = ref('history')
/** 抽屉全屏查看（地图 tab 提供切换入口，关闭抽屉时复位） */
const isFullscreen = ref(false)
/** 地图单点查看焦点（与 LocationMap 的 focusPoint prop 同构）：非空时地图只展示该历史点 */
const focusPoint = ref<MapFocusPoint | null>(null)
/** 轨迹采样密度选项（分钟），0=全部 */
const TRACK_INTERVAL_OPTIONS = [5, 10, 30, 60]
/** 轨迹采样密度（分钟），0=全部不传参；选择器在顶部筛选栏，值透传给 LocationMap */
const intervalMinute = ref(0)

function handleToggleFullscreen() {
  isFullscreen.value = !isFullscreen.value
}
/** 抽屉内容常驻（地图容器不可随开关销毁，Google SDK 依赖其存活），打开时重置 tab 并刷新列表 */
watch(() => props.modelValue, (value) => {
  if (value) {
    activeTab.value = 'history'
    focusPoint.value = null
    void refreshList()
    // 容器从 display:none 恢复显示后重算表格布局
    nextTick(() => {
      (tableRef.value?.tableRef as { doLayout?: () => void } | undefined)?.doLayout?.()
    })
  }
})

/** 切回地图 tab 时通知地图重算尺寸（pane 从 display:none 恢复显示）；
 *  老版本 element-plus 的 tab-click 首参是 pane 实例，paneName 为 ComputedRef，须取 .value 比较 */
function handleTabClick(pane: { paneName?: { value?: string } }) {
  if (pane?.paneName?.value === 'mapTrack') {
    mapRef.value?.handleVisible()
  }
}

/** 时间范围：默认近24小时；picker 精确到秒（YYYY-MM-DD HH:mm:ss），请求仍用秒级 start_time/end_time */
const [defaultStartTime, defaultEndTime] = getTimeRangeByEnum(TimeRange.HOURS_24)
const defaultDateRange: [string, string] = [
  dayjs.unix(defaultStartTime ?? Math.floor(dayjs().subtract(24, 'hour').valueOf() / 1000)).format('YYYY-MM-DD HH:mm:ss'),
  dayjs.unix(defaultEndTime ?? Math.floor(dayjs().valueOf() / 1000)).format('YYYY-MM-DD HH:mm:ss')
]
const timeRangeDate = ref<[string, string]>(defaultDateRange)
/** cmTable v-model 查询参数；page/limit 由 cmTable 内部写入/覆盖（limit 供滚动加载每页条数） */
const queryParams = reactive<{ sort: number; page: number; limit: number; start_time?: string; end_time?: string }>({
  sort: 0,
  page: 1,
  limit: 20,
  start_time: defaultStartTime !== undefined ? String(defaultStartTime) : undefined,
  end_time: defaultEndTime !== undefined ? String(defaultEndTime) : undefined
})

const drawerTitle = computed(() => {
  void locale.value
  return t('groupClient.locationInfoDrawerTitle', { name: props.terminalInfo?.computer_name ?? '' })
})

const SOURCE_LABELS = useLocationSourceLabels()

const tableOpt = computed<TableOpt>(() => {
  void locale.value
  return {
    path: 'list',
    totalPath: 'total',
    rowKey: 'id',
    height: '100%',
    bodyStyle: 'padding: 16px',
    containerOpt: { style: { flex: '1', minHeight: 0, overflow: 'hidden' } },
    showActionBar: false,
    showForm: false,
    showSearch: false,
    showRightToolbar: false,
    showTip: false,
    selection: false,
    usePagination: false,
    columns: [
      { prop: 'location_time', label: t('groupClient.locationInfoColTime'), minWidth: 170, formatter: formatLocationTime, columnOpt: { sortable: true } },
      { prop: 'longitude', label: t('groupClient.locationInfoColLongitude'), minWidth: 130, formatter: formatEmpty },
      { prop: 'latitude', label: t('groupClient.locationInfoColLatitude'), minWidth: 130, formatter: formatEmpty },
      { prop: 'source', label: t('groupClient.locationInfoColSource'), minWidth: 110, formatter: formatSource },
      { prop: 'client_ip', label: t('groupClient.locationInfoColIp'), minWidth: 140, formatter: formatEmpty },
      { prop: 'accuracy', label: t('groupClient.locationInfoColAccuracy'), minWidth: 110, formatter: formatAccuracy }
    ]
  }
})

/** 操作列：单点查看该记录在地图上的位置（无经纬度时禁用） */
const tableActOpt = computed<TableActOpt>(() => {
  void locale.value
  return {
    columnOpt: { label: t('groupClient.operation'), minWidth: 110, align: 'left', fixed: 'right' },
    actionButtons: (row?: List) => [
      {
        type: 'showOnMap',
        name: t('groupClient.locationInfoViewOnMap'),
        opt: { size: FormSize.Small, text: true, type: 'text' },
        disabled: () => !row?.longitude || !row?.latitude
      }
    ]
  }
})

/** 定位历史行操作：切到地图 tab 单点查看该记录位置 */
function handleAction({ type, rowItem }: { type: string; rowItem?: List }) {
  if (type !== 'showOnMap' || !rowItem || !rowItem.longitude || !rowItem.latitude) {
    return
  }
  focusPoint.value = {
    latitude: Number(rowItem.latitude),
    longitude: Number(rowItem.longitude),
    locationTime: rowItem.location_time
  }
  if (activeTab.value !== 'mapTrack') {
    activeTab.value = 'mapTrack'
  }
  nextTick(() => {
    mapRef.value?.handleVisible()
  })
}

/** 地图退出单点查看，恢复轨迹模式 */
function handleClearFocus() {
  focusPoint.value = null
}

const pageOption: PageOption = { pageNumName: 'page', pageSizeName: 'limit' }

/** 列表当前排序（0降序/1升序），导出沿用 */
const currentSort = ref<number>(0)

/** 请求时补上当前行的 device_id，兜底 list 为空数组；并将 cmTable 列排序（sort=列名+asc）转换为后端数字语义（0降序/1升序） */
function dataApi(params: Record<string, unknown>) {
  if (!props.terminalInfo?.device_id) {
    return Promise.resolve({ total: 0, list: [] })
  }
  const apiParams: Record<string, unknown> = { ...params }
  if (typeof apiParams.sort === 'string') {
    currentSort.value = apiParams.asc === true ? 1 : 0
    apiParams.sort = currentSort.value
  } else {
    currentSort.value = 0
  }
  delete apiParams.asc
  return service.getLocationHistoryList({
    ...apiParams,
    device_id: props.terminalInfo.device_id
  } as RequestParams).then(res => ({ ...res, list: res.list ?? [] }))
}

/** 导出当前筛选范围的定位历史（CSV/Excel） */
function download(type: ExportType) {
  if (!props.terminalInfo?.device_id) return
  void service.download(type, {
    device_id: props.terminalInfo.device_id,
    start_time: queryParams.start_time,
    end_time: queryParams.end_time,
    sort: currentSort.value
  }, t('groupClient.locationInfoExportFile'))
}

/** 时间范围变更：按天边界换算秒级起止；清空时回退默认近24小时 */
function handleTimeRangeChange(value: [string, string] | null) {
  let start: number | undefined
  let end: number | undefined
  if (!value || value.length !== 2 || !value[0] || !value[1]) {
    timeRangeDate.value = defaultDateRange
    start = defaultStartTime
    end = defaultEndTime
  } else {
    start = dayjs(value[0]).unix()
    end = dayjs(value[1]).unix()
  }
  queryParams.start_time = start !== undefined ? String(start) : undefined
  queryParams.end_time = end !== undefined ? String(end) : undefined
  // 时间范围变了，单点查看焦点失效，地图回到轨迹模式
  focusPoint.value = null
  void refreshList()
}

function handleClose() {
  isFullscreen.value = false
  emit('update:modelValue', false)
}

function formatEmpty(_row: unknown, value: unknown) {
  return value || '--'
}

function formatLocationTime(_row: unknown, value: unknown) {
  return formatTimeStamp(value as string) || '--'
}

function formatSource(_row: unknown, value: number) {
  return SOURCE_LABELS.value[value] ?? '--'
}

function formatAccuracy(_row: unknown, value: number) {
  return value > 0
    ? t('groupClient.locationInfoAccuracyValue', { value })
    : t('groupClient.locationInfoAccuracyUnknown')
}

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_option = _resolveComponent("el-option")!
  const _component_el_select = _resolveComponent("el-select")!
  const _component_el_date_picker = _resolveComponent("el-date-picker")!
  const _component_el_tab_pane = _resolveComponent("el-tab-pane")!
  const _component_el_tabs = _resolveComponent("el-tabs")!
  const _component_el_drawer = _resolveComponent("el-drawer")!

  return (_openBlock(), _createBlock(_component_el_drawer, {
    "append-to-body": "",
    "custom-class": "location-info-wrap",
    title: drawerTitle.value,
    "model-value": __props.modelValue,
    "show-close": "",
    size: isFullscreen.value ? 'calc(100% - 10px)' : '80%',
    onClose: handleClose
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            (focusPoint.value && activeTab.value === 'mapTrack')
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createElementVNode("span", _hoisted_4, _toDisplayString(_ctx.$t('groupClient.locationInfoColTime')) + ": " + _toDisplayString(_unref(formatPointTime)(focusPoint.value.locationTime)), 1),
                  _createVNode(_component_el_button, {
                    size: "mini",
                    type: "text",
                    class: "back-track-btn",
                    onClick: handleClearFocus
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t('groupClient.locationInfoBackToTrack')), 1)
                    ]),
                    _: 1
                  })
                ], 64))
              : _createCommentVNode("", true),
            (activeTab.value === 'mapTrack' && !focusPoint.value)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  _createElementVNode("span", _hoisted_5, _toDisplayString(_ctx.$t('groupClient.locationInfoTrackInterval')), 1),
                  _createVNode(_component_el_select, {
                    modelValue: intervalMinute.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((intervalMinute).value = $event)),
                    size: "small",
                    class: "interval-select"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_option, {
                        label: _ctx.$t('groupClient.locationInfoTrackIntervalAll'),
                        value: 0
                      }, null, 8, ["label"]),
                      (_openBlock(), _createElementBlock(_Fragment, null, _renderList(TRACK_INTERVAL_OPTIONS, (item) => {
                        return _createVNode(_component_el_option, {
                          key: item,
                          label: _ctx.$t('groupClient.locationInfoTrackIntervalValue', { value: item }),
                          value: item
                        }, null, 8, ["label", "value"])
                      }), 64))
                    ]),
                    _: 1
                  }, 8, ["modelValue"])
                ], 64))
              : _createCommentVNode("", true),
            _createVNode(_component_el_date_picker, {
              modelValue: timeRangeDate.value,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((timeRangeDate).value = $event)),
              type: "datetimerange",
              "unlink-panels": "",
              size: "small",
              style: {"width":"410px"},
              "value-format": "YYYY-MM-DD HH:mm:ss",
              "start-placeholder": _ctx.$t('common.startDate'),
              "end-placeholder": _ctx.$t('common.endDate'),
              onChange: handleTimeRangeChange
            }, null, 8, ["modelValue", "start-placeholder", "end-placeholder"])
          ]),
          _createElementVNode("div", _hoisted_6, [
            _createVNode(ExportDropdown, {
              "button-text-i18n-key": "groupClient.locationInfoExportRecord",
              "csv-i18n-key": "groupClient.locationInfoExportCsv",
              "excel-i18n-key": "groupClient.locationInfoExportExcel",
              disabled: !__props.terminalInfo.device_id,
              onExport: download
            }, null, 8, ["disabled"]),
            (activeTab.value === 'mapTrack')
              ? (_openBlock(), _createBlock(_component_el_button, {
                  key: 0,
                  circle: "",
                  size: "mini",
                  class: "fullscreen-btn",
                  title: isFullscreen.value ? _ctx.$t('groupClient.locationInfoExitFullscreen') : _ctx.$t('groupClient.locationInfoFullscreen'),
                  onClick: handleToggleFullscreen
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("i", {
                      class: _normalizeClass(isFullscreen.value ? 'el-icon-close' : 'el-icon-full-screen'),
                      "aria-hidden": "true"
                    }, null, 2)
                  ]),
                  _: 1
                }, 8, ["title"]))
              : _createCommentVNode("", true)
          ])
        ]),
        _createVNode(_component_el_tabs, {
          modelValue: activeTab.value,
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((activeTab).value = $event)),
          class: "tabs-wrap",
          onTabClick: handleTabClick
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_tab_pane, {
              label: _ctx.$t('groupClient.locationInfoTabHistory'),
              name: "history"
            }, {
              default: _withCtx(() => [
                _createVNode(CmTable, {
                  ref_key: "tableRef",
                  ref: tableRef,
                  modelValue: queryParams,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((queryParams) = $event)),
                  "table-opt": tableOpt.value,
                  "table-act-opt": tableActOpt.value,
                  api: dataApi,
                  "page-option": pageOption,
                  "first-loading": false,
                  onHandleAction: handleAction
                }, null, 8, ["modelValue", "table-opt", "table-act-opt"])
              ]),
              _: 1
            }, 8, ["label"]),
            _createVNode(_component_el_tab_pane, {
              label: _ctx.$t('groupClient.locationInfoTabMapTrack'),
              name: "mapTrack",
              lazy: ""
            }, {
              default: _withCtx(() => [
                _createVNode(LocationMap, {
                  ref_key: "mapRef",
                  ref: mapRef,
                  "device-id": __props.terminalInfo?.device_id,
                  "start-time": queryParams.start_time,
                  "end-time": queryParams.end_time,
                  "is-fullscreen": isFullscreen.value,
                  "focus-point": focusPoint.value,
                  "interval-minute": intervalMinute.value
                }, null, 8, ["device-id", "start-time", "end-time", "is-fullscreen", "focus-point", "interval-minute"])
              ]),
              _: 1
            }, 8, ["label"])
          ]),
          _: 1
        }, 8, ["modelValue"])
      ])
    ]),
    _: 1
  }, 8, ["title", "model-value", "size"]))
}
}

})